import { SERVICE_VIDEO_MAX_SECONDS } from '@makhe/shared';

export const VIDEO_MAX_BYTES = 50 * 1024 * 1024;

export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/*';

export function safeStorageFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-60);
}

export function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(v.duration) ? v.duration : 0);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    v.src = url;
  });
}

/** Grab a representative frame as a JPEG poster. */
export function captureVideoPoster(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const done = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    const fail = () => done(null);
    // The poster is optional. Some mobile browsers (notably iOS Safari) can
    // silently never fire canplay/seeked for certain clips, which would hang the
    // caller (the upload flow) forever. Bail after a few seconds instead.
    timer = setTimeout(fail, 8000);

    video.onloadedmetadata = () => {
      const target = Math.min(1, (video.duration || 2) / 2);
      const seek = () => {
        try {
          video.currentTime = target;
        } catch {
          fail();
        }
      };
      if (video.readyState >= 2) seek();
      else video.oncanplay = seek;
    };

    video.onseeked = () => {
      try {
        const maxW = 720;
        const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale) || maxW;
        canvas.height = Math.round(video.videoHeight * scale) || Math.round((maxW * 16) / 9);
        const ctx = canvas.getContext('2d');
        if (!ctx) return fail();
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => done(blob), 'image/jpeg', 0.82);
      } catch {
        fail();
      }
    };

    video.onerror = fail;
    video.src = url;
  });
}

export async function assertShortVideo(
  file: File,
  maxSeconds = SERVICE_VIDEO_MAX_SECONDS,
): Promise<number> {
  if (!file.type.startsWith('video/')) {
    throw new Error('Pick a video file.');
  }
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error('Max 50 MB. Compress your video to 720p first.');
  }
  const duration = await readVideoDuration(file);
  if (duration > maxSeconds + 2) {
    throw new Error(`Videos must be ≤ ${maxSeconds}s. Trim and re-upload.`);
  }
  return duration;
}
